import type { ReactNode, Ref } from 'react';
import type {
  ContentObject,
  Media,
} from '../../lib/flotiq-client/types/api-response';
import type {
  VariantsGridCreateFn,
  VariantsGridDeleteFn,
} from './VariantsGrid/VariantsGrid';

import { useMemo } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import {
  getCtdFeaturedImage,
  getMediaUrl,
} from '../../lib/flotiq-client/api-helpers';
import { EMPTY_OBJECT } from '../../lib/globals';
import { getTestProps, isImagePreviewSupported } from '../../lib/helpers';

import {
  ArrowUpRightIcon,
  DeleteIcon,
  LinkIcon,
  PencilIcon,
  ReplaceIcon,
} from '../../images/shapes';

import Button from '../Button/Button';
import Card from '../Card/Card';
import StatusPill from '../StatusPill/StatusPill';
import Tooltip from '../Tooltip/Tooltip';
import VariantsGrid from './VariantsGrid/VariantsGrid';

type RelationProps = Media | ContentObject | Record<string, any>;

export type RelationShowFn = (relation: RelationProps) => void;

export interface RelationCardProps {
  /**
   * Reference to the card container
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * Relation object
   */
  relation?: RelationProps;
  /**
   * Relation object type
   */
  relationType?: string;
  /**
   * Relation type field used to get object title
   */
  titleField?: string;
  /**
   * Content type featured image url
   */
  featuredImageUrl?: string;
  /**
   * If featured image is loading
   */
  featuredImageLoading?: boolean;
  /**
   * If relations are loading
   */
  relationsAreLoading?: boolean;
  /**
   * Relation card image overlay
   */
  imageOverlay?: ReactNode;
  /**
   * Relation card image container
   */
  thumbnail?: ReactNode;
  /**
   * On delete relation callback
   */
  onDelete?: () => void;
  /**
   * On edit relation callback
   */
  onEdit?: () => void;
  /**
   * On swap relation callback
   */
  onSwap?: () => void;
  /**
   * On show relation callback
   */
  onShow?: RelationShowFn;
  /**
   * If relation actions should be disabled
   */
  disabled?: boolean;
  /**
   * On variant create callback
   */
  onVariantCreate?: VariantsGridCreateFn;
  /**
   * On variant delete callback
   */
  onVariantDelete?: VariantsGridDeleteFn;
  /**
   * Element to drag the card
   */
  dragElement?: ReactNode;
  /**
   * Additional grid classes
   */
  additionalContainerClasses?: string;
  /**
   * Relations card test id
   */
  testId?: string;
  /**
   * If object status should be shown
   */
  withObjectStatus?: boolean;
  /**
   * If the copy id button should be visible
   */
  isCopyButton?: boolean;
}

const RelationCard = ({
  ref,
  relation = EMPTY_OBJECT,
  relationType = '',
  titleField = '',
  featuredImageUrl = '',
  featuredImageLoading = false,
  relationsAreLoading = false,
  imageOverlay = '',
  thumbnail = '',
  onDelete,
  onEdit,
  onShow,
  onSwap,
  disabled = false,
  onVariantCreate,
  onVariantDelete,
  dragElement,
  withObjectStatus,
  isCopyButton = true,
  additionalContainerClasses = '',
  testId = '',
}: RelationCardProps) => {
  const { t } = useTranslation();
  const isImage = relation.type === 'image';
  const isMedia = relationType === '_media';

  const title =
    relation.internal?.objectTitle || relation[titleField] || relation.id;

  const imageTitleField = isMedia ? relation.title : '';

  const imagePreviewSupported = isImagePreviewSupported(relation.extension);
  const getMediaUrlOnSupported = imagePreviewSupported
    ? getMediaUrl(relation, 280)
    : '';

  const mediaUrl = isImage ? getMediaUrlOnSupported : '';
  const relationUrl = isMedia
    ? mediaUrl
    : featuredImageUrl || getCtdFeaturedImage(relationType);

  const relationTypeToShow = isMedia ? 'Media' : relationType;

  const hasVariants =
    isMedia && isImage && imagePreviewSupported && onVariantCreate;

  const cardThumbnail = useMemo(() => {
    if (thumbnail) return thumbnail;

    return isMedia &&
      (relationsAreLoading ||
        (isImage && !imagePreviewSupported) ||
        !isImage) ? (
      <div className="flex items-center justify-center h-full w-full">
        <div
          className={twMerge(
            'flex items-center justify-center rounded-full bg-slate-700 h-12 w-12',
            relationsAreLoading ? 'animate-pulse' : 'bg-blue-300',
          )}
        >
          {relationsAreLoading ? '' : relation.extension || t('Global.NA')}
        </div>
      </div>
    ) : (
      ''
    );
  }, [
    thumbnail,
    isMedia,
    relationsAreLoading,
    isImage,
    relation.extension,
    imagePreviewSupported,
    t,
  ]);

  const fullSizeImage = useMemo(() => {
    return (
      isMedia &&
      !relationsAreLoading &&
      onShow &&
      relation.id &&
      relation.extension && (
        <div className="hidden group-hover:flex absolute top-2 right-2">
          <Button
            buttonColor="gray"
            iconColor="blue"
            testId={'open-full-size-media'}
            iconImage={<ArrowUpRightIcon className="w-3" />}
            additionalClasses="shrink-0"
            onClick={(event) => {
              event.stopPropagation();
              onShow(relation);
            }}
          />
        </div>
      )
    );
  }, [isMedia, onShow, relation, relationsAreLoading]);

  return (
    <div ref={ref} className="flex w-full">
      <div
        className={twMerge(
          'w-full border border-slate-300 dark:border-slate-700',
          dragElement ? 'rounded-l-lg' : 'rounded-lg',
          additionalContainerClasses,
        )}
      >
        <Card
          body={
            relationsAreLoading ? (
              <div
                className="animate-pulse h-full flex items-center"
                {...getTestProps(testId, 'card-loading')}
              >
                <div className="grid grid-rows-2 gap-2 w-full">
                  <div className="h-3.5 my-[3.5px] w-3/4 bg-slate-400 rounded-sm" />
                  <div className="h-3.5 my-[3.5px] w-1/2 bg-slate-500 rounded-sm" />
                </div>
              </div>
            ) : (
              <div
                className="grid grid-rows-[fit-content(20px)_1fr] xs:grid-rows-1 xs:grid-cols-[1fr_fit-content(120px)] 
                        gap-1.5 sm:gap-2 h-full"
              >
                <div className="self-center space-y-1.5 sm:space-y-2 min-w-0 max-w-full">
                  {imageTitleField && (
                    <div
                      className="truncate dark:text-white font-bold"
                      title={imageTitleField}
                    >
                      <CopyToClipboard
                        text={imageTitleField}
                        onCopy={() => {
                          toast.success(
                            t('ObjectsOfType.Copied', {
                              text: imageTitleField,
                            }),
                          );
                        }}
                        {...getTestProps(
                          testId,
                          `copy-image-title-${relation.id}`,
                        )}
                      >
                        <span className="cursor-copy">{imageTitleField}</span>
                      </CopyToClipboard>
                    </div>
                  )}
                  <div className="truncate dark:text-white" title={title}>
                    <CopyToClipboard
                      text={title}
                      onCopy={() => {
                        toast.success(
                          t('ObjectsOfType.Copied', { text: title }),
                        );
                      }}
                      {...getTestProps(testId, `copy-title-${relation.id}`)}
                    >
                      <span className="cursor-copy">{title}</span>
                    </CopyToClipboard>
                  </div>
                  <div className="truncate text-sm text-slate-400 pr-5">
                    {relationTypeToShow}
                  </div>
                </div>
                <div className="order-first xs:order-0 flex flex-col sm:items-center gap-2 xs:my-1.5">
                  {(onEdit || onSwap || onDelete || isCopyButton) && (
                    <div className="flex w-fit gap-1 bg-white dark:bg-slate-700 rounded-xl h-6 px-2 items-center">
                      {onEdit && (
                        <Tooltip
                          tooltip={t('Global.Edit')}
                          tooltipPlacement="leftCenter"
                          phoneTooltipPlacement="leftCenter"
                        >
                          <Button
                            iconImage={
                              <PencilIcon className="text-blue dark:text-blue-400 w-3.5" />
                            }
                            buttonColor="borderless"
                            onClick={onEdit}
                            additionalClasses="p-1 hover:opacity-50"
                            {...getTestProps(
                              testId,
                              `edit-${relationType}-${relation.id}`,
                              'testId',
                            )}
                          />
                        </Tooltip>
                      )}
                      {onSwap && (
                        <Tooltip
                          tooltip={t('Global.Replace')}
                          tooltipPlacement="leftCenter"
                          phoneTooltipPlacement="leftCenter"
                        >
                          <Button
                            iconImage={
                              <ReplaceIcon className="text-lime-600 dark:text-lime-500 hover:opacity-50 w-3.5" />
                            }
                            buttonColor="borderless"
                            onClick={onSwap}
                            disabled={disabled}
                            additionalClasses="p-1 hover:opacity-50"
                            {...getTestProps(
                              testId,
                              `swap-${relationType}-${relation.id}`,
                              'testId',
                            )}
                          />
                        </Tooltip>
                      )}
                      {onDelete && (
                        <Tooltip
                          tooltip={t('Global.Delete')}
                          tooltipPlacement="leftCenter"
                          phoneTooltipPlacement="leftCenter"
                        >
                          <Button
                            iconImage={
                              <DeleteIcon className="h-3.5 text-red-600 dark:text-orange-500" />
                            }
                            buttonColor="borderless"
                            additionalClasses="p-1 hover:opacity-50"
                            onClick={onDelete}
                            disabled={disabled}
                            {...getTestProps(
                              testId,
                              `delete-${relationType}-${relation.id}`,
                              'testId',
                            )}
                          />
                        </Tooltip>
                      )}
                      {isCopyButton && (
                        <Tooltip
                          tooltip={t('ObjectsOfType.CopyIdTooltip')}
                          tooltipPlacement="leftCenter"
                          phoneTooltipPlacement="leftCenter"
                        >
                          <CopyToClipboard
                            text={relation.id}
                            onCopy={() =>
                              toast.success(
                                t('ObjectsOfType.Copied', {
                                  text: relation.id,
                                }),
                              )
                            }
                            {...getTestProps(testId, 'copy-id')}
                          >
                            <Button
                              iconImage={
                                <LinkIcon className="h-3.5 text-slate-700 dark:text-slate-400" />
                              }
                              buttonColor="borderless"
                              additionalClasses="p-1 hover:opacity-50"
                              disabled={disabled}
                              {...getTestProps(testId, `copy-id`, 'testId')}
                            />
                          </CopyToClipboard>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {withObjectStatus && relation.internal?.status && (
                    <StatusPill
                      size="sm"
                      color={relation.internal.status}
                      status={t(
                        `ObjectStatus.Status.${relation.internal.status}`,
                      )}
                      containerClasses="inline-flex items-center justify-center xs:ml-auto xs:mt-auto"
                    />
                  )}
                </div>
              </div>
            )
          }
          imageUrl={relationsAreLoading ? '' : relationUrl}
          imageUrlLoading={isMedia ? false : featuredImageLoading}
          thumbnail={cardThumbnail}
          imageOverlay={imageOverlay || fullSizeImage}
          additionalContainerClasses={twMerge(
            'bg-slate-50 dark:bg-slate-800 w-full xs:h-24 flex',
            imageTitleField ? 'h-36' : 'h-32',
            hasVariants && 'rounded-b-none',
            dragElement && 'rounded-r-none',
          )}
          additionalImageClasses={twMerge(isMedia ? 'w-fit h-fit' : 'w-full')}
          additionalImageContainerClasses="w-1/3 max-w-[150px] min-w-[110px] dark:bg-slate-700"
          additionalBodyClasses="px-2 xs:px-4 py-2 w-2/3 grow h-full"
          horizontal
          {...getTestProps(testId, '', 'testId')}
        />
        {hasVariants && (
          <VariantsGrid
            relation={relation as Media}
            onVariantCreate={onVariantCreate}
            onVariantDelete={onVariantDelete}
            testId={testId}
          />
        )}
      </div>
      {dragElement}
    </div>
  );
};

export default RelationCard;
